import React, { useState, useEffect } from 'react';
import { CheckSquare, Building2, TrendingUp, Clock, Target, Award, ArrowRight, Calendar, Users, BarChart3 } from 'lucide-react';
import { api } from '../utils/api';

function Dashboard({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      const data = await api.get('/assignments');
      const myAssignments = data.filter(a => a.assignedToUserId === user.id);
      setAssignments(myAssignments);
    } catch (err) {
      console.error('Failed to load assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const activeAssignments = assignments.filter(a => a.status !== 'COMPLETED');
  const completedAssignments = assignments.filter(a => a.status === 'COMPLETED');
  const overdueAssignments = assignments.filter(a => a.status === 'OVERDUE');

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, var(--primary-blue-600) 0%, var(--primary-blue-700) 100%)',
        borderRadius: '12px',
        padding: '3rem 2.5rem',
        marginBottom: '2rem',
        color: 'var(--white)',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: '0.875rem', fontWeight: '500', opacity: 0.9, marginBottom: '0.5rem' }}>
              {getGreeting()},
            </p>
            <h1 style={{ 
              fontFamily: "'Work Sans', sans-serif", 
              fontSize: '2.25rem', 
              fontWeight: '700', 
              marginBottom: '0.75rem',
              letterSpacing: '-0.5px'
            }}>
              {user.name}
            </h1>
            <p style={{ fontSize: '1rem', opacity: 0.9 }}>
              Welcome to your command center. You're ready to excel.
            </p>
          </div>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <Award size={44} color="white" strokeWidth={2} />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div className="card" style={{ padding: '1.5rem', border: '1px solid var(--gray-200)' }}>
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Active Checklists
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--primary-blue-700)', marginBottom: '0.25rem', lineHeight: 1 }}>
                {activeAssignments.length}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                In progress
              </div>
            </div>
            <div style={{ 
              padding: '0.875rem', 
              backgroundColor: 'var(--primary-blue-100)', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Clock size={28} color="var(--primary-blue-600)" strokeWidth={2.5} />
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: '1.5rem', border: '1px solid var(--gray-200)' }}>
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Completed
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '700', color: 'var(--success-700)', marginBottom: '0.25rem', lineHeight: 1 }}>
                {completedAssignments.length}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                Finished strong
              </div>
            </div>
            <div style={{ 
              padding: '0.875rem', 
              backgroundColor: 'var(--success-100)', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Target size={28} color="var(--success-600)" strokeWidth={2.5} />
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: '1.5rem', border: '1px solid var(--gray-200)' }}>
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Your Role
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--gray-900)', marginBottom: '0.25rem', lineHeight: 1.2 }}>
                {user.role.replace('_', ' ')}
              </div>
              {user.propertyAssignments && user.propertyAssignments.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  {user.propertyAssignments.length} {user.propertyAssignments.length === 1 ? 'property' : 'properties'}
                </div>
              )}
            </div>
            <div style={{ 
              padding: '0.875rem', 
              backgroundColor: 'var(--info-100)', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Building2 size={28} color="var(--info-600)" strokeWidth={2.5} />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-header">
          <div>
            <h2 className="card-title">Quick Actions</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
              Jump into what matters most
            </p>
          </div>
          <TrendingUp size={24} color="var(--primary-blue-600)" strokeWidth={2} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          <button 
            onClick={() => document.querySelector('[data-testid=\"nav-my-checklists\"]').click()}
            style={{
              padding: '1.5rem',
              backgroundColor: 'var(--primary-blue-50)',
              border: '1px solid var(--primary-blue-200)',
              borderRadius: '10px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 150ms ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--primary-blue-100)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--primary-blue-50)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <CheckSquare size={24} color="var(--primary-blue-600)" strokeWidth={2} />
              <ArrowRight size={20} color="var(--primary-blue-600)" />
            </div>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.375rem' }}>
              My Checklists
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
              {activeAssignments.length > 0 
                ? `${activeAssignments.length} active ${activeAssignments.length === 1 ? 'checklist' : 'checklists'} waiting`
                : 'View your assignments'}
            </p>
          </button>

          {['AREA_MANAGER', 'REGIONAL', 'VP', 'EXEC', 'ADMIN'].includes(user.role) && (
            <button 
              onClick={() => document.querySelector('[data-testid=\"nav-team\"]').click()}
              style={{
                padding: '1.5rem',
                backgroundColor: 'var(--success-50)',
                border: '1px solid var(--success-200)',
                borderRadius: '10px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 150ms ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--success-100)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--success-50)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <Users size={24} color="var(--success-600)" strokeWidth={2} />
                <ArrowRight size={20} color="var(--success-600)" />
              </div>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.375rem' }}>
                Team Management
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                Assign and monitor your team's progress
              </p>
            </button>
          )}

          {user.role === 'ADMIN' && (
            <button 
              onClick={() => document.querySelector('[data-testid=\"nav-admin-dashboard\"]').click()}
              style={{
                padding: '1.5rem',
                backgroundColor: 'var(--warning-50)',
                border: '1px solid var(--warning-200)',
                borderRadius: '10px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 150ms ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--warning-100)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--warning-50)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <BarChart3 size={24} color="var(--warning-600)" strokeWidth={2} />
                <ArrowRight size={20} color="var(--warning-600)" />
              </div>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.375rem' }}>
                System Analytics
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                View organization-wide insights
              </p>
            </button>
          )}
        </div>
      </div>

      {/* Welcome Message / Status */}
      {activeAssignments.length > 0 ? (
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, var(--success-100) 0%, var(--success-50) 100%)',
          border: '1px solid var(--success-200)'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '1.5rem' }}>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--success-600)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <CheckSquare size={32} color="white" strokeWidth={2.5} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.5rem' }}>
                You've got {activeAssignments.length} active {activeAssignments.length === 1 ? 'checklist' : 'checklists'}
              </h3>
              <p style={{ fontSize: '0.9375rem', color: 'var(--gray-700)', marginBottom: '1rem' }}>
                Keep the momentum going! Each item completed brings you closer to mastery.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => document.querySelector('[data-testid=\"nav-my-checklists\"]').click()}
              >
                Continue Progress <ArrowRight size={16} style={{ marginLeft: '0.5rem' }} />
              </button>
            </div>
          </div>
        </div>
      ) : completedAssignments.length > 0 ? (
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, var(--primary-blue-100) 0%, var(--primary-blue-50) 100%)',
          border: '1px solid var(--primary-blue-200)'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '1.5rem' }}>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--success-600)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Award size={32} color="white" strokeWidth={2.5} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.5rem' }}>
                All caught up!
              </h3>
              <p style={{ fontSize: '0.9375rem', color: 'var(--gray-700)' }}>
                You've completed all your checklists. Excellent work! Check back later for new assignments.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'start', gap: '1.5rem' }}>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--info-100)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Calendar size={32} color="var(--info-600)" strokeWidth={2.5} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.5rem' }}>
                Ready for action
              </h3>
              <p style={{ fontSize: '0.9375rem', color: 'var(--gray-700)' }}>
                No checklists assigned yet. When your manager assigns work, it'll appear here.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overdue Alert */}
      {overdueAssignments.length > 0 && (
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, var(--danger-100) 0%, var(--danger-50) 100%)',
          border: '2px solid var(--danger-600)',
          marginTop: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '1.5rem' }}>
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--danger-600)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Clock size={32} color="white" strokeWidth={2.5} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--danger-700)', marginBottom: '0.5rem' }}>
                {overdueAssignments.length} {overdueAssignments.length === 1 ? 'checklist' : 'checklists'} overdue
              </h3>
              <p style={{ fontSize: '0.9375rem', color: 'var(--gray-700)', marginBottom: '1rem' }}>
                These need your immediate attention. Let's get them back on track!
              </p>
              <button 
                className="btn btn-danger"
                onClick={() => document.querySelector('[data-testid=\"nav-my-checklists\"]').click()}
              >
                Review Now <ArrowRight size={16} style={{ marginLeft: '0.5rem' }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
